<template>
  <HeaderContent title="Cleaning" />
  <ActionContent
    :cta="false"
    ctaText="Cleaning"
    @action="modal = !modal"
    :filterDate="false"
    @showFilter="modalFilter = !modalFilter"
    @search-query="searchData"
  />
  <CleaningStatistics title="Cleaning Statistics" :data="statistics" />
  <CleaningForm :modal="modal" @update:modal="updateModal" />

  <!-- Modal untuk Cleaning Finish Confirmation -->
  <CleaningConfirm
    title="Cleaning Finish Confirmation"
    v-model="modalTime"
    @confirmed="saveFinish"
  >
    <template #text>
      <div class="cleaning-confirmation">
        <!-- Room Information Section -->
        <div class="info-section">
          <h6>Room Information</h6>
          <table class="w-full text-left border-collapse">
            <tbody>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">Room Number</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ activeRow?.no_room || "-" }}
                </td>
              </tr>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">Room Type</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ activeRow?.type_room || "-" }}
                </td>
              </tr>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">Date</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ activeRow?.date || "-" }}
                </td>
              </tr>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">Done By</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ activeRow?.housekeeping || "-" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cleaning Details Section -->
        <div class="info-section">
          <h6>Cleaning Details</h6>

          <table class="w-full text-left border-collapse">
            <tbody>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">Start Time</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ startTime || "-" }}
                </td>
              </tr>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">End Time</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ endTime || "-" }}
                </td>
              </tr>
              <tr class="border-b border-transparent">
                <td class="text-left pr-2">Duration</td>
                <td class="text-left pr-2">:</td>
                <td class="text-left fw-semibold">
                  {{ duration || "-" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Remarks Section -->
        <div class="form-section">
          <h6>Additional Information</h6>
          <BFormGroup label="Remarks (Optional)" label-for="remarks-input">
            <BFormTextarea
              id="remarks-input"
              v-model="remarks"
              placeholder="Enter any remarks or notes about the cleaning process..."
              rows="3"
            />
          </BFormGroup>
        </div>
      </div>
    </template>
  </CleaningConfirm>

  <CleaningDataTable
    :data="cleanings"
    :header="headers"
    :currentPage="currentPage"
    :custom-actions="customActions"
  />
</template>

<script>
import { inject, ref, onMounted } from "vue";
import { BFormGroup, BFormTextarea } from "bootstrap-vue-next";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import CleaningForm from "./CleaningForm.vue";
import CleaningConfirm from "@/components/ModalDialog.vue";
import CleaningStatistics from "@/components/StatisticData.vue";
import CleaningDataTable from "@/components/DataTable.vue";
import iconToday from "../../../../public/icons/sun.svg";
import iconWeek from "../../../../public/icons/week.svg";

export default {
  name: "CleaningIndex",
  components: {
    BFormGroup,
    BFormTextarea,
    HeaderContent,
    ActionContent,
    CleaningStatistics,
    CleaningForm,
    CleaningConfirm,
    CleaningDataTable,
  },
  setup() {
    const repositories = inject("repositories");
    const scheduleRepository = repositories?.schedule;
    const cleaningRepository = repositories?.cleaning;

    const cleanings = ref([]);
    const modal = ref(false);
    const modalTime = ref(false);
    const modalFilter = ref(false);
    const currentPage = ref(1);
    const isSaving = ref(false);

    const activeRow = ref(null);
    const startTime = ref(null);
    const endTime = ref(null);
    const duration = ref(null);
    const remarks = ref("");

    const headers = ref([
      { key: "no", label: "#" },
      { key: "no_room", label: "Room Number", sortable: true },
      { key: "type_room", label: "Room Type", sortable: true },
      { key: "date", label: "Date", sortable: true },
      { key: "start_time", label: "Start Time", sortable: true },
      { key: "end_time", label: "End Time", sortable: true },
      { key: "duration", label: "Duration", sortable: true },
      { key: "housekeeping", label: "Done By", sortable: true },
      { key: "remarks", label: "Remarks", sortable: true },
      { key: "action", label: "Actions" },
    ]);

    // Fungsi untuk menghitung durasi
    const calculateDuration = (startTime, endTime) => {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      let totalMinutes = eh * 60 + em - (sh * 60 + sm);

      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }

      if (totalMinutes >= 60) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours} jam ${minutes} menit`;
      } else {
        return `${totalMinutes} menit`;
      }
    };

    // Fungsi untuk update item secara lokal tanpa reload semua data
    const updateLocalItem = (scheduleUuid, roomUuid, updates) => {
      const index = cleanings.value.findIndex(
        (item) =>
          item.schedule_uuid === scheduleUuid && item.room_uuid === roomUuid
      );

      if (index !== -1) {
        cleanings.value[index] = {
          ...cleanings.value[index],
          ...updates,
        };
      }
    };

    const customActions = ref([
      {
        label: (item) =>
          item.status_cleaning === "start"
            ? "Finish"
            : item.status_cleaning === "finish"
            ? "Done"
            : "Start",

        icon: "fas fa-stopwatch",
        variant: (item) =>
          item.status_cleaning === "start"
            ? "danger"
            : item.status_cleaning === "finish"
            ? "secondary"
            : "success",

        action: async (item) => {
          console.log("Action clicked for item:", item);

          if (item.status_cleaning === "finish") {
            console.log("Item is already finished, no action needed");
            return;
          }

          const now = new Date();
          const currentTime = now.toTimeString().slice(0, 5);

          if (item.status_cleaning === "idle") {
            // START CLEANING - Update UI immediately
            updateLocalItem(item.schedule_uuid, item.room_uuid, {
              status_cleaning: "start",
              start_time: currentTime,
            });

            try {
              // Then call API in background
              await cleaningRepository.start({
                schedule_uuid: item.schedule_uuid,
                room_uuid: item.room_uuid,
                start_time: currentTime,
              });

              console.log("Cleaning started successfully");
            } catch (error) {
              console.error("Error starting cleaning:", error);
              // Revert local state if API fails
              updateLocalItem(item.schedule_uuid, item.room_uuid, {
                status_cleaning: "idle",
                start_time: "-",
              });
            }
          } else if (item.status_cleaning === "start") {
            // FINISH CLEANING - Prepare modal data immediately
            const start = item.start_time;

            if (!start || start === "-" || !start.includes(":")) {
              console.warn("Start time is invalid. Cannot proceed to finish.");
              return;
            }

            // Set all modal data immediately
            startTime.value = start;
            endTime.value = currentTime;
            activeRow.value = item;
            duration.value = calculateDuration(start, currentTime);

            // Show modal immediately
            modalTime.value = true;
          }
        },

        disabled: (item) => item.status_cleaning === "finish",
      },
    ]);

    const statistics = ref([
      {
        label: "Total",
        key: "Today",
        value: 3,
        color: "primary",
        icon: iconToday,
      },
      {
        label: "Total",
        key: "Week",
        value: 8,
        color: "primary",
        icon: iconWeek,
      },
    ]);

    // Fungsi untuk mendapatkan data cleaning dari API
    const getCleaningData = async () => {
      try {
        const cleaningRes = await cleaningRepository.getAll();
        console.log("Raw cleaning API response:", cleaningRes);
        return cleaningRes.data?.data || [];
      } catch (error) {
        console.error("Error fetching cleaning data:", error);
        return [];
      }
    };

    // Fungsi untuk menentukan status cleaning berdasarkan data yang ada
    const determineCleaningStatus = (cleaningData) => {
      if (!cleaningData || Object.keys(cleaningData).length === 0) {
        return "idle";
      }

      const { start_time, end_time } = cleaningData;

      const isValidTime = (time) => {
        return (
          time &&
          time !== "-" &&
          time !== "" &&
          time !== null &&
          time !== undefined &&
          time !== "null"
        );
      };

      if (isValidTime(start_time) && isValidTime(end_time)) {
        return "finish";
      }

      if (isValidTime(start_time)) {
        return "start";
      }

      return "idle";
    };

    const getAll = async (searchQuery = "") => {
      const params = {};

      if (searchQuery) params.search = searchQuery;

      try {
        const [scheduleRes, cleaningDataArray] = await Promise.all([
          scheduleRepository.getAll(params),
          getCleaningData(),
        ]);

        const cleaningMap = {};
        cleaningDataArray.forEach((cleaning) => {
          const key = `${cleaning.schedule_uuid}-${cleaning.room_uuid}`;
          cleaningMap[key] = cleaning;
        });

        const tempData = [];

        if (scheduleRes.data?.data) {
          scheduleRes.data.data.forEach((item) => {
            const staffName = item.employee?.fullname ?? "-";
            const scheduleDate = item.schedule ?? "-";

            item.room_category?.forEach((category) => {
              const roomType = category?.name ?? "-";

              category.rooms?.forEach((room) => {
                const cleaningKey = `${item.uuid}-${room.uuid}`;
                const cleaningData = cleaningMap[cleaningKey] || {};

                const status_cleaning = determineCleaningStatus(cleaningData);

                const formatTime = (time) => {
                  if (!time || time === null || time === "null") return "-";
                  return time.length > 5 ? time.substring(0, 5) : time;
                };

                tempData.push({
                  no_room: room?.code ?? "-",
                  type_room: roomType,
                  date: scheduleDate,
                  housekeeping: staffName,
                  start_time: formatTime(cleaningData.start_time),
                  end_time: formatTime(cleaningData.end_time),
                  duration: cleaningData.duration ?? "-",
                  remarks: cleaningData.remarks ?? "-",
                  uuid: cleaningData.uuid ?? null,
                  schedule_uuid: item.uuid,
                  room_uuid: room.uuid,
                  status_cleaning,
                });
              });
            });
          });
        }

        cleanings.value = tempData.map((item, index) => ({
          no: index + 1,
          ...item,
        }));
      } catch (err) {
        console.error("Failed to fetch cleaning data:", err);
      }
    };

    const updateModal = (newValue) => {
      modal.value = newValue;
    };

    const resetModal = () => {
      remarks.value = "";
      activeRow.value = null;
      startTime.value = null;
      endTime.value = null;
      duration.value = null;
      isSaving.value = false;
    };

    const saveFinish = async () => {
      if (!activeRow.value) {
        console.error("No active row found");
        return;
      }

      isSaving.value = true;

      // Update local state immediately for better UX
      updateLocalItem(
        activeRow.value.schedule_uuid,
        activeRow.value.room_uuid,
        {
          status_cleaning: "finish",
          end_time: endTime.value,
          duration: duration.value,
          remarks: remarks.value || "-",
        }
      );

      try {
        await cleaningRepository.finish({
          schedule_uuid: activeRow.value.schedule_uuid,
          room_uuid: activeRow.value.room_uuid,
          end_time: endTime.value,
          duration: duration.value,
          remarks: remarks.value || "-",
        });

        console.log("Cleaning finished successfully");

        // Close modal and reset
        modalTime.value = false;
        resetModal();
      } catch (error) {
        console.error("Error finishing cleaning:", error);

        // Revert local state if API fails
        updateLocalItem(
          activeRow.value.schedule_uuid,
          activeRow.value.room_uuid,
          {
            status_cleaning: "start",
            end_time: "-",
            duration: "-",
            remarks: "-",
          }
        );

        modalTime.value = false;
      } finally {
        isSaving.value = false;
      }
    };

    const searchData = async (keyword) => {
      await getAll(keyword);
    };

    onMounted(() => {
      getAll();
    });

    return {
      cleanings,
      headers,
      modal,
      modalTime,
      modalFilter,
      currentPage,
      customActions,
      statistics,
      startTime,
      endTime,
      duration,
      remarks,
      activeRow,
      isSaving,
      saveFinish,
      getAll,
      updateModal,
      searchData,
      resetModal,
    };
  },
};
</script>

<style scoped>
/* Styling untuk modal cleaning confirmation */
.cleaning-confirmation {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial,
    sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.info-section {
  margin-bottom: 24px;
}

.info-section h6 {
  color: #666;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.info-grid {
  /* display: grid; */
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.info-item strong {
  color: #333;
  font-weight: 500;
  min-width: 120px;
  margin-right: 8px;
}

.form-section {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.form-section h6 {
  color: #666;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
}

/* Responsive untuk mobile */
@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-item strong {
    min-width: auto;
    margin-bottom: 2px;
  }
}

/* Form styling */
.form-group label {
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-control {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
}

.form-control:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}
</style>
